import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import Avatar from "@/components/Avatar";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, MessageCircle, Trash2, ArrowUpRight, Sparkles, Globe } from "lucide-react";
import CardEditor from "@/components/CardEditor";
import WelcomeProfileModal from "@/components/WelcomeProfileModal";
import TrialBanner from "@/components/TrialBanner";

const MOOD_LABEL = {
  great: "Great",
  good: "Good",
  neutral: "Steady",
  stressed: "Stressed",
  frustrated: "Frustrated",
  exhausted: "Exhausted",
  anxious: "Anxious",
  sad: "Heavy",
};

function moodWordFromAvg(avg) {
  if (avg >= 0.4) return "Bright";
  if (avg >= 0.15) return "Steady";
  if (avg > -0.15) return "Mixed";
  if (avg > -0.4) return "Heavy";
  return "Hard";
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [personas, setPersonas] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [mood, setMood] = useState({ entries: [], average: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const [cardPersona, setCardPersona] = useState(null);

  const reload = async () => {
    try {
      const [p, c, m] = await Promise.all([
        api.get("/personas"),
        api.get("/conversations"),
        api.get("/mood?days=30"),
      ]);
      setPersonas(p.data);
      setConversations(c.data);
      setMood(m.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startChat = async (persona, kind = "chat") => {
    setStarting(persona.persona_id);
    try {
      const { data } = await api.post("/conversations", {
        persona_id: persona.persona_id,
        kind,
        title:
          kind === "daily"
            ? `Day check-in with ${persona.name}`
            : `Chat with ${persona.name}`,
      });
      navigate(`/chat/${data.conversation_id}`, {
        state: { greet: kind === "daily" ? "daily" : "chat" },
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setStarting(null);
    }
  };

  const deletePersona = async (persona_id) => {
    if (!window.confirm("Delete this Colleague and all chats with them?")) return;
    try {
      await api.delete(`/personas/${persona_id}`);
      toast.success("Persona removed");
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-[#FBF9F6]">
      <WelcomeProfileModal />
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <TrialBanner />
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12 anim-fade-up">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#9A938A] mb-2">
              Today's workplace
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#2D2926]">
              Hello, {user?.name?.split(" ")[0] || "friend"}.
            </h1>
            <p className="mt-2 text-[#6B655D] max-w-xl">
              Talk through the day, get advice, or ask for feedback. Your Colleagues are here.
            </p>
          </div>
          <Link
            to="/personas/new"
            data-testid="new-persona-link"
            className="inline-flex items-center gap-2 bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-5 py-2.5 text-sm transition-colors"
          >
            <Plus size={16} strokeWidth={1.5} /> New Colleague
          </Link>
        </div>

        {/* Mood snapshot strip */}
        <div className="surface-card p-6 mb-12 anim-fade-up anim-delay-1 flex flex-col sm:flex-row sm:items-center gap-4 justify-between bg-[#F2EFE9]">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#9A938A] mb-1">
              Last 30 days
            </p>
            <p
              data-testid="mood-summary"
              className="font-serif text-3xl text-[#2D2926]"
            >
              Your weeks feel{" "}
              <span className="italic text-[#C29260]">{moodWordFromAvg(mood.average)}</span>
              <span className="text-base text-[#6B655D] ml-2">
                · {mood.count} check-in{mood.count === 1 ? "" : "s"}
              </span>
            </p>
          </div>
          <Link
            to="/mood"
            data-testid="open-mood-link"
            className="inline-flex items-center gap-1 text-[#2D2926] hover:text-[#C29260] text-sm font-medium"
          >
            See vibe trend <ArrowUpRight size={16} strokeWidth={1.5} />
          </Link>
        </div>

        {/* Personas */}
        <section className="mb-16">
          <div className="flex items-end justify-between mb-6">
            <h2 className="font-serif text-3xl text-[#2D2926]">Your Colleagues</h2>
            <span className="text-xs uppercase tracking-wider text-[#9A938A]">
              {personas.length} total
            </span>
          </div>

          {loading ? (
            <div className="text-[#6B655D]">Loading…</div>
          ) : personas.length === 0 ? (
            <EmptyPersonas />
          ) : (
            <div
              data-testid="personas-grid"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {personas.map((p, i) => (
                <article
                  key={p.persona_id}
                  data-testid={`persona-card-${p.persona_id}`}
                  className={`surface-card p-6 anim-fade-up anim-delay-${Math.min(i + 1, 4)} flex flex-col gap-4 transition-all hover:-translate-y-1`}
                >
                  <div className="flex items-start gap-4">
                    <Avatar src={p.avatar_url} name={p.name} size={56} />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-serif text-2xl text-[#2D2926] truncate">
                        {p.name}
                      </h3>
                      <p className="text-sm text-[#6B655D] truncate">
                        {p.role || "Colleague"}
                        {p.industry ? ` · ${p.industry}` : ""}
                      </p>
                      {(p.domain || p.is_hr_advisor) && (
                        <span
                          data-testid={`domain-badge-${p.persona_id}`}
                          className="inline-block mt-1.5 text-[10px] uppercase tracking-[0.18em] text-[#C29260] bg-[#F2EFE9] rounded-full px-2 py-0.5"
                        >
                          {(p.domain || "hr").charAt(0).toUpperCase() +
                            (p.domain || "hr").slice(1)}
                          -aware
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => deletePersona(p.persona_id)}
                      data-testid={`delete-persona-${p.persona_id}`}
                      className="text-[#9A938A] hover:text-[#D98074] transition-colors"
                      aria-label="Delete persona"
                    >
                      <Trash2 size={16} strokeWidth={1.5} />
                    </button>
                  </div>
                  {p.tagline && (
                    <p className="text-sm text-[#6B655D] leading-relaxed italic">
                      {p.tagline}
                    </p>
                  )}
                  {p.expertise_areas?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {p.expertise_areas.slice(0, 4).map((a) => (
                        <span
                          key={a}
                          className="text-xs px-2.5 py-1 rounded-full bg-[#F2EFE9] text-[#6B655D]"
                        >
                          {a}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="mt-auto flex flex-wrap gap-2 pt-2">
                    <button
                      type="button"
                      data-testid={`start-chat-${p.persona_id}`}
                      onClick={() => startChat(p, "chat")}
                      disabled={starting === p.persona_id}
                      className="inline-flex items-center gap-1 bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-4 py-2 text-sm transition-colors disabled:opacity-60"
                    >
                      <MessageCircle size={14} strokeWidth={1.5} /> Chat
                    </button>
                    <button
                      type="button"
                      data-testid={`start-daily-${p.persona_id}`}
                      onClick={() => startChat(p, "daily")}
                      disabled={starting === p.persona_id}
                      className="inline-flex items-center gap-1 bg-[#F2EFE9] text-[#2D2926] hover:bg-[#E6E2D8] rounded-full px-4 py-2 text-sm transition-colors disabled:opacity-60"
                    >
                      <Sparkles size={14} strokeWidth={1.5} /> Talk about my day
                    </button>
                    <button
                      type="button"
                      data-testid={`make-card-${p.persona_id}`}
                      onClick={() => setCardPersona(p)}
                      className="inline-flex items-center gap-1 text-[#6B655D] hover:text-[#2D2926] hover:bg-[#F2EFE9] rounded-full px-3 py-2 text-sm transition-colors"
                      title="Make a public Colleague Card"
                    >
                      <Globe size={14} strokeWidth={1.5} />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Recent conversations */}
        <section>
          <h2 className="font-serif text-3xl text-[#2D2926] mb-6">Recent conversations</h2>
          {conversations.length === 0 ? (
            <p className="text-[#6B655D]">
              No conversations yet. Start a chat with a Colleague above.
            </p>
          ) : (
            <ul
              data-testid="recent-conversations"
              className="surface-card divide-y divide-[#E6E2D8] overflow-hidden"
            >
              {conversations.slice(0, 8).map((c) => (
                <li key={c.conversation_id}>
                  <Link
                    to={`/chat/${c.conversation_id}`}
                    data-testid={`conv-link-${c.conversation_id}`}
                    className="flex items-center gap-4 px-6 py-4 hover:bg-[#F2EFE9] transition-colors"
                  >
                    <Avatar src={c.persona_avatar} name={c.persona_name} size={36} />
                    <div className="flex-1 min-w-0">
                      <p className="text-[#2D2926] truncate">{c.title}</p>
                      <p className="text-xs text-[#9A938A] uppercase tracking-wider">
                        {new Date(c.updated_at).toLocaleString()}
                      </p>
                    </div>
                    <ArrowUpRight
                      size={16}
                      strokeWidth={1.5}
                      className="text-[#9A938A]"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      {cardPersona && (
        <CardEditor persona={cardPersona} onClose={() => setCardPersona(null)} />
      )}
    </div>
  );
}

function EmptyPersonas() {
  return (
    <div
      data-testid="personas-empty"
      className="surface-card p-12 text-center bg-white"
    >
      <h3 className="font-serif text-3xl text-[#2D2926] mb-2">
        Let's build your first Colleague.
      </h3>
      <p className="text-[#6B655D] mb-6 max-w-md mx-auto">
        Name them, give them a role, pick a tone. Or meet one of the workplace specialists.
      </p>
      <Link
        to="/personas/new"
        className="inline-flex items-center gap-2 bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-6 py-3 text-sm transition-colors"
      >
        <Plus size={16} strokeWidth={1.5} /> Create a Colleague
      </Link>
    </div>
  );
}
